import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const buildAuthHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'X-Company-ID': localStorage.getItem('company_id') || '',
    'X-User-ID': localStorage.getItem('user_id') || '',
  },
});

/**
 * Combobox مشترك لحقول تصنيف الموظف (القسم / المسمى الوظيفي).
 * يدعم شكلَي استجابة:
 *   - الجديد (Section 3.3): [{ id, name, name_en, ... }]
 *   - القديم (PR #119):     { values: ["..."] }
 *
 * Props:
 *   value, onChange         — controlled string (الاسم المعروض / المخزّن)
 *   onChangeMeta(name, raw) — اختياري: يُستدعى مع العنصر الكامل عند الاختيار من القائمة
 *   fetchUrl                — يدعم cascade (يعاد الجلب عند تغيّر الـ URL)
 *   items                   — اختياري: قائمة جاهزة من الأب (يتجاوز fetchUrl)
 *                             الشكل: [{ id, name, raw? }] أو [{ id, name, ... }]
 *   eager                   — اختياري: اجلب البيانات عند التحميل بدلاً من الانتظار للـ popover
 *   createUrl, createPayload — اختياري: إذا حُدِّد فالضغط على "+ إضافة" يُرسل POST بالقيمة
 *   placeholder
 *   disabled, disabledMessage — لتعطيل الحقل التابع قبل اختيار الأب
 *   className, testId
 */
export default function EmployeeFieldCombobox({
  value,
  onChange,
  onChangeMeta,
  fetchUrl,
  items: itemsFromParent,
  eager = false,
  createUrl,
  createPayload,
  placeholder = '',
  disabled = false,
  disabledMessage = '',
  className,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [creating, setCreating] = useState(false);
  const lastFetchedUrlRef = useRef(null);

  // When the parent supplies `items` directly, mirror them and skip
  // the network. This is how EmployeesPage prefetches departments and
  // positions so the dropdown is populated the moment it opens.
  const usingParentItems = Array.isArray(itemsFromParent);

  useEffect(() => {
    if (!usingParentItems) return;
    setItems(
      itemsFromParent
        .filter((d) => d && d.name)
        .map((d) => ({ id: d.id || null, name: d.name, raw: d.raw ?? d }))
    );
    setErrored(false);
    lastFetchedUrlRef.current = '__parent__';
  }, [itemsFromParent, usingParentItems]);

  useEffect(() => {
    if (usingParentItems) return;
    // Eager mode fetches on mount/url-change; lazy mode waits for popover.
    if (!eager && !open) return;
    if (disabled || errored) return;
    if (lastFetchedUrlRef.current === fetchUrl) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}${fetchUrl}`, buildAuthHeaders())
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.data)
          ? res.data
              .filter((d) => d && d.name)
              .map((d) => ({ id: d.id || null, name: d.name, raw: d }))
          : Array.isArray(res.data?.values)
            ? res.data.values
                .filter((v) => typeof v === 'string' && v.trim())
                .map((v) => ({ id: null, name: v, raw: v }))
            : [];
        setItems(list);
        lastFetchedUrlRef.current = fetchUrl;
      })
      .catch(() => {
        if (cancelled) return;
        setErrored(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, fetchUrl, errored, disabled, eager, usingParentItems]);

  // إذا تغيّر fetchUrl (cascade) — أعد الـ cache
  useEffect(() => {
    if (usingParentItems) return;
    lastFetchedUrlRef.current = null;
    setItems([]);
    setErrored(false);
  }, [fetchUrl, usingParentItems]);

  const handleSelect = (name, raw) => {
    onChange(name);
    if (typeof onChangeMeta === 'function') onChangeMeta(name, raw);
    setOpen(false);
  };

  const handleCreate = async () => {
    const trimmed = (value || '').trim();
    if (!trimmed) return;
    if (!createUrl) {
      onChange(trimmed);
      if (typeof onChangeMeta === 'function') onChangeMeta(trimmed, null);
      setOpen(false);
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_URL}${createUrl}`,
        { name: trimmed, ...(createPayload || {}) },
        buildAuthHeaders()
      );
      const newItem = res.data && res.data.id
        ? { id: res.data.id, name: res.data.name || trimmed, raw: res.data }
        : { id: null, name: trimmed, raw: null };
      setItems((prev) => [...prev, newItem]);
      handleSelect(newItem.name, newItem.raw);
    } catch {
      // fallback: store as free text only
      onChange(trimmed);
      if (typeof onChangeMeta === 'function') onChangeMeta(trimmed, null);
      setOpen(false);
    } finally {
      setCreating(false);
    }
  };

  // Disabled fallback — render plain text input or message
  if (disabled) {
    return (
      <div
        data-testid={testId}
        className={cn(
          'flex h-10 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm text-right text-muted-foreground',
          className
        )}
      >
        <span className="flex-1 text-right truncate">
          {value || disabledMessage || placeholder}
        </span>
      </div>
    );
  }

  // Network failure fallback — degrade to plain Input
  if (errored) {
    return (
      <Input
        className={cn('text-right', className)}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
      />
    );
  }

  const trimmed = (value || '').trim();
  const showAddOption =
    trimmed.length > 0 &&
    !items.some((it) => it.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm text-right',
            'ring-offset-background placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          <span
            className={cn(
              'flex-1 text-right truncate',
              !value && 'text-muted-foreground'
            )}
          >
            {value || placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <Command
          shouldFilter={true}
          filter={(itemValue, search) =>
            itemValue.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
          }
        >
          <CommandInput
            placeholder="ابحث أو اكتب قيمة جديدة..."
            value={value || ''}
            onValueChange={(v) => onChange(v)}
            dir="rtl"
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>جارٍ التحميل...</span>
              </div>
            ) : (
              <>
                <CommandEmpty>اكتب لإضافة قيمة جديدة</CommandEmpty>
                {items.length > 0 && (
                  <CommandGroup>
                    {items.map((it) => (
                      <CommandItem
                        key={it.id || it.name}
                        value={it.name}
                        onSelect={() => handleSelect(it.name, it.raw)}
                        className="text-right data-[selected=true]:bg-ds-primary-50 data-[selected=true]:text-ds-primary-700 dark:data-[selected=true]:bg-ds-primary-900/30 dark:data-[selected=true]:text-ds-primary-300"
                      >
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            value === it.name ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                        <span className="flex-1 text-right">{it.name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                {showAddOption && (
                  <CommandGroup heading="إضافة جديد">
                    <CommandItem
                      value={`__add__${trimmed}`}
                      disabled={creating}
                      onSelect={handleCreate}
                      className="text-right text-primary"
                    >
                      {creating && (
                        <Loader2 className="ml-2 h-3 w-3 animate-spin" />
                      )}
                      <span className="flex-1 text-right">+ "{trimmed}"</span>
                    </CommandItem>
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
